import { create } from 'zustand';

interface CallState {
  callChannelId: string | null;
  isCallActive: boolean;
  callVaultId: string | null;
  voiceParticipants: Record<string, any[]>;
  peerVoiceStates: Record<string, { isMuted: boolean; isCameraOn: boolean; isScreenSharing: boolean }>;
  isPipMode: boolean;
  pipPosition: { x: number; y: number };
  isDraggingPip: boolean;
  isSidebarCollapsed: boolean;
  isCinemaMode: boolean;
  isVaultBrowserCollapsed: boolean;
  activeSpeakerId: string;
  isMediaPlayerPoppedOut: boolean;
  layoutMode: 'focus' | 'tile';
  showSideChat: boolean;
  focusedParticipantId: string | null;
  hiddenParticipants: string[];
  isMuted: boolean;
  sbVolume: number;
  callReactions: any[];
  isDeafened: boolean;
  isVideoOn: boolean;
  isAudioProcessingEnabled: boolean;
  isLimiterEnabled: boolean;
  isNormalizerEnabled: boolean;
  limiterThreshold: number;
  normalizerThreshold: number;
  outputGain: number;
  isNoiseSuppressionEnabled: boolean;
  // Video background effect — mirror of the AudioProcessor chain for the
  // video plane. `none` = pass-through, `blur` = CSS-style canvas filter on
  // the local feed (cheap, no MediaPipe needed), `picture` = full MediaPipe
  // Selfie Segmentation with a chosen background image composited behind the
  // user. `videoEffectBg` is the public/ path of the selected image;
  // `null` means "no image selected yet". `videoEffectIntensity` carries the
  // 0-20px blur radius for the Soft Focus mode (parallels V1's
  // blurIntensity slider by the same scale). Hoisted here so App.tsx's
  // VideoProcessor lifecycle effect can subscribe to changes without
  // prop-drilling through CallLayout / CallLayoutWrapper.
  videoEffectMode: 'none' | 'blur' | 'picture';
  videoEffectBg: string | null;
  videoEffectIntensity: number;
  showAudioSettings: boolean;
  showScreenShareOptions: boolean;
  isScreenSharing: boolean;
  callDuration: number;
  dmCallState: 'idle' | 'calling' | 'ringing';
  dmCallPeer: { id: string; name: string; avatar: string } | null;
  remoteStreams: Record<string, MediaStream>;
  remoteScreenStreams: Record<string, MediaStream>;

  // Actions
  setCallChannelId: (id: string | null) => void;
  setIsCallActive: (active: boolean) => void;
  setCallVaultId: (id: string | null) => void;
  setVoiceParticipants: (updater: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)) => void;
  setPeerVoiceStates: (updater: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setIsPipMode: (active: boolean) => void;
  setPipPosition: (pos: { x: number; y: number } | ((prev: { x: number; y: number }) => { x: number; y: number })) => void;
  setIsDraggingPip: (dragging: boolean) => void;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  setIsCinemaMode: (cinema: boolean) => void;
  setIsVaultBrowserCollapsed: (collapsed: boolean) => void;
  setActiveSpeakerId: (id: string) => void;
  setIsMediaPlayerPoppedOut: (popped: boolean) => void;
  setLayoutMode: (mode: 'focus' | 'tile') => void;
  setShowSideChat: (show: boolean) => void;
  setFocusedParticipantId: (id: string | null) => void;
  setHiddenParticipants: (updater: string[] | ((prev: string[]) => string[])) => void;
  setIsMuted: (muted: boolean) => void;
  setSbVolume: (volume: number) => void;
  setCallReactions: (updater: any[] | ((prev: any[]) => any[])) => void;
  setIsDeafened: (deafened: boolean) => void;
  setIsVideoOn: (on: boolean) => void;
  setIsAudioProcessingEnabled: (enabled: boolean) => void;
  setIsLimiterEnabled: (enabled: boolean) => void;
  setIsNormalizerEnabled: (enabled: boolean) => void;
  setLimiterThreshold: (val: number) => void;
  setNormalizerThreshold: (val: number) => void;
  setOutputGain: (val: number) => void;
  setIsNoiseSuppressionEnabled: (enabled: boolean) => void;
  setVideoEffectMode: (mode: 'none' | 'blur' | 'picture') => void;
  setVideoEffectBg: (src: string | null) => void;
  setVideoEffectIntensity: (n: number) => void;
  setShowAudioSettings: (show: boolean) => void;
  setShowScreenShareOptions: (show: boolean) => void;
  setIsScreenSharing: (sharing: boolean) => void;
  setCallDuration: (duration: number | ((prev: number) => number)) => void;
  setDmCallState: (state: 'idle' | 'calling' | 'ringing') => void;
  setDmCallPeer: (peer: { id: string; name: string; avatar: string } | null) => void;
  setRemoteStreams: (updater: Record<string, MediaStream> | ((prev: Record<string, MediaStream>) => Record<string, MediaStream>)) => void;
  setRemoteScreenStreams: (updater: Record<string, MediaStream> | ((prev: Record<string, MediaStream>) => Record<string, MediaStream>)) => void;
}

export const useCallStore = create<CallState>((set) => ({
  callChannelId: null,
  isCallActive: false,
  callVaultId: null,
  voiceParticipants: {},
  peerVoiceStates: {},
  isPipMode: false,
  pipPosition: { x: 24, y: 80 },
  isDraggingPip: false,
  isSidebarCollapsed: false,
  isCinemaMode: false,
  isVaultBrowserCollapsed: false,
  activeSpeakerId: 'glitch',
  isMediaPlayerPoppedOut: false,
  layoutMode: 'focus',
  showSideChat: false,
  focusedParticipantId: null,
  hiddenParticipants: [],
  isMuted: false,
  sbVolume: 0.05,
  callReactions: [],
  isDeafened: false,
  isVideoOn: false,
  isAudioProcessingEnabled: true,
  isLimiterEnabled: true,
  isNormalizerEnabled: true,
  limiterThreshold: -1,
  normalizerThreshold: -24,
  outputGain: 1.0,
  isNoiseSuppressionEnabled: true,
  videoEffectMode: 'none',
  videoEffectBg: null,
  videoEffectIntensity: 8,
  showAudioSettings: false,
  showScreenShareOptions: false,
  isScreenSharing: false,
  callDuration: 0,
  dmCallState: 'idle',
  dmCallPeer: null,
  remoteStreams: {},
  remoteScreenStreams: {},

  setCallChannelId: (callChannelId) => set({ callChannelId }),
  setIsCallActive: (isCallActive) => set({ isCallActive }),
  setCallVaultId: (callVaultId) => set({ callVaultId }),
  setVoiceParticipants: (updater) => set((state) => ({
    voiceParticipants: typeof updater === 'function' ? updater(state.voiceParticipants) : updater
  })),
  setPeerVoiceStates: (updater) => set((state) => ({
    peerVoiceStates: typeof updater === 'function' ? updater(state.peerVoiceStates) : updater
  })),
  setIsPipMode: (isPipMode) => set({ isPipMode }),
  setPipPosition: (updater) => set((state) => ({
    pipPosition: typeof updater === 'function' ? updater(state.pipPosition) : updater
  })),
  setIsDraggingPip: (isDraggingPip) => set({ isDraggingPip }),
  setIsSidebarCollapsed: (isSidebarCollapsed) => set({ isSidebarCollapsed }),
  setIsCinemaMode: (isCinemaMode) => set({ isCinemaMode }),
  setIsVaultBrowserCollapsed: (isVaultBrowserCollapsed) => set({ isVaultBrowserCollapsed }),
  setActiveSpeakerId: (activeSpeakerId) => set({ activeSpeakerId }),
  setIsMediaPlayerPoppedOut: (isMediaPlayerPoppedOut) => set({ isMediaPlayerPoppedOut }),
  setLayoutMode: (layoutMode) => set({ layoutMode }),
  setShowSideChat: (showSideChat) => set({ showSideChat }),
  setFocusedParticipantId: (focusedParticipantId) => set({ focusedParticipantId }),
  setHiddenParticipants: (updater) => set((state) => ({
    hiddenParticipants: typeof updater === 'function' ? updater(state.hiddenParticipants) : updater
  })),
  setIsMuted: (isMuted) => set({ isMuted }),
  setSbVolume: (sbVolume) => set({ sbVolume }),
  setCallReactions: (updater) => set((state) => ({
    callReactions: typeof updater === 'function' ? updater(state.callReactions) : updater
  })),
  setIsDeafened: (isDeafened) => set({ isDeafened }),
  setIsVideoOn: (isVideoOn) => set({ isVideoOn }),
  setIsAudioProcessingEnabled: (isAudioProcessingEnabled) => set({ isAudioProcessingEnabled }),
  setIsLimiterEnabled: (isLimiterEnabled) => set({ isLimiterEnabled }),
  setIsNormalizerEnabled: (isNormalizerEnabled) => set({ isNormalizerEnabled }),
  setLimiterThreshold: (limiterThreshold) => set({ limiterThreshold }),
  setNormalizerThreshold: (normalizerThreshold) => set({ normalizerThreshold }),
  setOutputGain: (outputGain) => set({ outputGain }),
  setIsNoiseSuppressionEnabled: (isNoiseSuppressionEnabled) => set({ isNoiseSuppressionEnabled }),
  setVideoEffectMode: (videoEffectMode) => set({ videoEffectMode }),
  setVideoEffectBg: (videoEffectBg) => set({ videoEffectBg }),
  setVideoEffectIntensity: (videoEffectIntensity) => set({ videoEffectIntensity }),
  setShowAudioSettings: (showAudioSettings) => set({ showAudioSettings }),
  setShowScreenShareOptions: (showScreenShareOptions) => set({ showScreenShareOptions }),
  setIsScreenSharing: (isScreenSharing) => set({ isScreenSharing }),
  setCallDuration: (updater) => set((state) => ({
    callDuration: typeof updater === 'function' ? updater(state.callDuration) : updater
  })),
  setDmCallState: (dmCallState) => set({ dmCallState }),
  setDmCallPeer: (dmCallPeer) => set({ dmCallPeer }),
  setRemoteStreams: (updater) => set((state) => ({
    remoteStreams: typeof updater === 'function' ? updater(state.remoteStreams) : updater
  })),
  setRemoteScreenStreams: (updater) => set((state) => ({
    remoteScreenStreams: typeof updater === 'function' ? updater(state.remoteScreenStreams) : updater
  }))
}));
